<template>
  <div>
    <!-- Credentials shown once after approval -->
    <b-modal
      id="creds-modal"
      title="Vet login created"
      hide-footer
      size="md"
      @hide="clearCreds"
    >
      <div v-if="creds">
        <p class="mb-2">
          Share these login details with <strong>{{ creds.name }}</strong>. The password is shown only once.
        </p>
        <b-list-group class="mb-1">
          <b-list-group-item class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Username</span>
            <strong>{{ creds.user_name }}</strong>
          </b-list-group-item>
          <b-list-group-item class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Password</span>
            <strong>{{ creds.password }}</strong>
          </b-list-group-item>
          <b-list-group-item class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Email</span>
            <span>{{ creds.email }}</span>
          </b-list-group-item>
        </b-list-group>
        <b-button
          variant="primary"
          block
          @click="copyCreds"
        >
          Copy details
        </b-button>
      </div>
    </b-modal>

    <b-card no-body>
      <div class="d-flex justify-content-between align-items-center p-2 border-bottom">
        <h4 class="mb-0">
          Vet approvals
        </h4>
        <b-badge
          variant="light-primary"
          pill
        >
          {{ vets.length }} pending
        </b-badge>
      </div>

      <div
        v-if="loading"
        class="text-center p-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!vets.length"
        class="text-center text-muted p-3"
      >
        No vets are waiting for approval.
      </div>

      <b-table
        v-else
        :items="vets"
        :fields="fields"
        responsive
        class="mb-0"
      >
        <template #cell(requested_by)="{ item }">
          <b-badge
            v-for="(c, i) in item.requested_by"
            :key="i"
            variant="light-secondary"
            class="mr-25"
          >{{ c }}</b-badge>
          <span
            v-if="!item.requested_by || !item.requested_by.length"
            class="text-muted"
          >—</span>
        </template>
        <template #cell(actions)="{ item }">
          <b-button
            size="sm"
            variant="success"
            class="mr-1"
            :disabled="busyId === item.id"
            @click="approve(item)"
          >
            Approve
          </b-button>
          <b-button
            size="sm"
            variant="outline-danger"
            :disabled="busyId === item.id"
            @click="reject(item)"
          >
            Reject
          </b-button>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { getUserData, isSuperAdminUser } from '@/auth/utils'
import {
	BCard, BTable, BButton, BBadge, BSpinner, BModal, BListGroup, BListGroupItem,
} from 'bootstrap-vue'

export default {
	components: {
		BCard, BTable, BButton, BBadge, BSpinner, BModal, BListGroup, BListGroupItem,
	},
	data() {
		return {
			vets: [],
			loading: true,
			busyId: null,
			creds: null,
			fields: [
				{ key: 'name', label: 'Vet' },
				{ key: 'specialization', label: 'Specialization' },
				{ key: 'phone', label: 'Phone' },
				{ key: 'requested_by', label: 'Requested by' },
				{ key: 'actions', label: '' },
			],
		}
	},
	created() {
		if (!isSuperAdminUser(getUserData())) {
			this.$router.replace('/')
			return
		}
		this.fetchPending()
	},
	methods: {
		async fetchPending() {
			this.loading = true
			try {
				const { data } = await axios.get('web/vet-approvals')
				this.vets = data.data || []
			} catch (e) {
				this.toast('Failed to load pending vets', 'danger')
			} finally {
				this.loading = false
			}
		},
		async approve(item) {
			const ok = await this.$bvModal.msgBoxConfirm(
				`Approve ${item.name} and create their login? They will be able to sign in and answer consultations.`,
				{
					title: 'Approve vet', okVariant: 'success', okTitle: 'Approve', cancelTitle: 'Cancel',
				},
			)
			if (!ok) return
			this.busyId = item.id
			try {
				const { data } = await axios.post(`web/vet-approvals/${item.id}/approve`, {})
				this.creds = { ...data.credentials, name: item.name }
				this.vets = this.vets.filter(v => v.id !== item.id)
				this.$bvModal.show('creds-modal')
			} catch (e) {
				this.toast(e.response?.data?.message || 'Approval failed', 'danger')
			} finally {
				this.busyId = null
			}
		},
		async reject(item) {
			const ok = await this.$bvModal.msgBoxConfirm(
				`Reject ${item.name}? They will not get a login.`,
				{
					title: 'Reject vet', okVariant: 'danger', okTitle: 'Reject', cancelTitle: 'Cancel',
				},
			)
			if (!ok) return
			this.busyId = item.id
			try {
				await axios.post(`web/vet-approvals/${item.id}/reject`, {})
				this.vets = this.vets.filter(v => v.id !== item.id)
				this.toast(`${item.name} rejected`, 'secondary')
			} catch (e) {
				this.toast(e.response?.data?.message || 'Reject failed', 'danger')
			} finally {
				this.busyId = null
			}
		},
		copyCreds() {
			if (!this.creds) return
			const text = `Poultrac vet login\nUsername: ${this.creds.user_name}\nPassword: ${this.creds.password}\nEmail: ${this.creds.email}`
      navigator.clipboard?.writeText(text)
      this.toast('Login details copied', 'success')
		},
		clearCreds() {
			this.creds = null
		},
		toast(title, variant = 'primary') {
			this.$bvToast.toast(title, { title: 'Vet approvals', variant, solid: true })
		},
	},
}
</script>
